import React, { useCallback } from 'react';
import womanSchedulingImg from '../../../../../images/woman-scheduling.png';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { SelectOption } from '../../../../../components/Select';
import { Select } from '../../../../../components/Select';
import { humanize } from '../../../../../utils/humanize';
import { User } from '../../../../../entities/User';
import { Candidate } from '../../../../../entities/applicant_tracking/Candidate';
import { InterviewParticipants } from './InterviewParticipants';
import { VcsProvider } from '../../../../../enums/IntegrationProvider/VcsProvider';
import { InterviewerParticipant } from '../../../../../entities/applicant_tracking/InterviewerParticipant';

interface PropTypes {
  title: string;
  candidate: Candidate;
  participants: InterviewerParticipant[];
  setParticipants: (participants: InterviewerParticipant[]) => void;
  availableParticipants: User[];
  durationMinutes: number;
  setDurationMinutes: (duration: number) => void;
  meetingProvider: VcsProvider;
  setMeetingProvider: (provider: VcsProvider) => void;
  meetingProviders: VcsProvider[];
  customImage?: string;
  description?: string;
  candidateView: boolean;
  onChangingMeetingOrganizer: (participant: InterviewerParticipant) => void;
  allowChangingMeetingOrganizer: boolean;
  allowIgnoreParticipantCalendar: boolean;
}

const AVAILABLE_INTERVIEW_DURATION_IN_MINUTES = [15, 30, 45, 60, 90];

function InterviewDuration(props: {
  durationMinutes: number;
  candidateView?: boolean;
  setDurationMinutes: (duration: number) => void;
}) {
  const availableDurationInMinutes =
    AVAILABLE_INTERVIEW_DURATION_IN_MINUTES.map((duration) => {
      return {
        value: duration.toString(),
        label: `${duration} mins`,
        selected: duration === props.durationMinutes,
      };
    });
  const selectedDurationInMinutes = availableDurationInMinutes.find(
    (option) => option.selected,
  );

  const onDurationSelection = useCallback(
    (option: SelectOption) => {
      props.setDurationMinutes(+option.value);
    },
    [props.durationMinutes],
  );

  return (
    <div data-testid='interview_duration' className={'mt-4 w-60'}>
      <p className='text-dark-500 fs-5 mb-2'>Interview Duration</p>
      {props.candidateView ? (
        <p className={'text-dark-200 fs-6 mt-1'}>
          {props.durationMinutes} mins
        </p>
      ) : (
        <Select
          options={availableDurationInMinutes}
          placeholder='Select duration'
          onChange={onDurationSelection}
          selected={selectedDurationInMinutes}
          menuPlacement='top'
          size='sm'
          className={'fs-6'}
          isClearable={false}
        />
      )}
    </div>
  );
}

function InterviewMeetingProvider(props: {
  meetingProvider?: VcsProvider;
  setMeetingProvider: (provider: VcsProvider | null) => void;
  meetingProviders: VcsProvider[];
  candidateView?: boolean;
}) {
  const availableMeetingProviders = props.meetingProviders.map((provider) => {
    return {
      value: provider,
      label: humanize(provider),
      selected: provider === props.meetingProvider,
    };
  });

  const selectedDurationInMinutes = availableMeetingProviders.find(
    (option) => option.selected,
  );

  return (
    <div data-testid='interview_meeting_provider' className={'mt-3 w-60'}>
      <p className='text-dark-500 fs-5 mb-2'>Video Conferencing</p>
      {props.candidateView ? (
        <p className='text-dark-200 fs-6 mt-1'>
          {humanize(props.meetingProvider)}
        </p>
      ) : (
        <Select
          options={availableMeetingProviders}
          placeholder='Select provider'
          size='sm'
          menuPlacement='top'
          onChange={(option) => {
            props.setMeetingProvider(option?.value as VcsProvider);
          }}
          selected={selectedDurationInMinutes}
          className={'fs-6'}
          isClearable={true}
        />
      )}
    </div>
  );
}

function Description(props: { description: string }) {
  if (props.description == null) {
    return null;
  }

  return (
    <div className={classNames('d-flex justify-content-center')}>
      <div
        className={classNames(
          'text-center fs-6 pt-1 text-gray-700',
          styles['description-text'],
        )}
      >
        {props.description}
      </div>
    </div>
  );
}

export function InterviewDetails(props: PropTypes) {
  return (
    <>
      <div className={'text-center'}>
        <img
          src={props.customImage || womanSchedulingImg}
          style={{ maxWidth: '250px' }}
        />
      </div>
      <h2 className={'mt-4 text-center'}>{props.title}</h2>
      <Description description={props.description} />
      <div
        className={classNames(
          'd-flex',
          'flex-column',
          'overflow-scroll',
          styles['details'],
        )}
      >
        <InterviewParticipants {...props} />
        <InterviewDuration
          durationMinutes={props.durationMinutes}
          candidateView={props.candidateView}
          setDurationMinutes={props.setDurationMinutes}
        />
        <InterviewMeetingProvider
          meetingProvider={props.meetingProvider}
          setMeetingProvider={props.setMeetingProvider}
          candidateView={props.candidateView}
          meetingProviders={props.meetingProviders}
        />
      </div>
    </>
  );
}
